import { useEffect, useState } from "react";

import {
  AdminCluster,
  PatchClusterIn,
  useAdminClusterMembers,
  useAdminClusters,
  usePatchCluster,
  useRunClustering,
} from "../../api/feedback";

const CLUSTER_STATUS_OPTIONS: AdminCluster["status"][] = [
  "open",
  "in_progress",
  "shipped",
  "rejected",
];

const STATUS_TONE: Record<AdminCluster["status"], string> = {
  open: "bg-sky-500/15 text-sky-300",
  in_progress: "bg-amber-500/20 text-amber-200",
  shipped: "bg-emerald-500/15 text-emerald-300",
  rejected: "bg-slate-700/40 text-slate-500",
};

function fmt(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function ClusterList() {
  const clusters = useAdminClusters();
  const run = useRunClustering();
  const [openId, setOpenId] = useState<number | null>(null);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3">
        <p className="text-xs text-slate-500">
          Group similar items via the LLM. Manual-trigger only — no cron.
        </p>
        <button
          onClick={() => run.mutate()}
          disabled={run.isPending}
          className="rounded-lg bg-sky-500 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-sky-400 disabled:opacity-50"
        >
          {run.isPending ? "Clustering…" : "Re-cluster pending"}
        </button>
      </div>

      {run.isError && (
        <div className="mb-3 rounded-lg border border-rose-900 bg-rose-950/40 p-3 text-xs text-rose-300">
          {run.error.message}
        </div>
      )}
      {run.data && (
        <div className="mb-3 rounded-lg border border-emerald-900/60 bg-emerald-950/20 p-3 text-xs text-emerald-200">
          Clustered {run.data.items_considered} items into {run.data.clusters.length} group
          {run.data.clusters.length === 1 ? "" : "s"}.
        </div>
      )}

      {clusters.isLoading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : (clusters.data ?? []).length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-800 p-6 text-sm text-slate-500">
          No clusters yet. Run the LLM pass once you have a handful of items.
        </div>
      ) : (
        <ul className="space-y-2">
          {clusters.data!.map((c) => (
            <ClusterRow
              key={c.id}
              cluster={c}
              open={openId === c.id}
              onToggle={() => setOpenId(openId === c.id ? null : c.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function ClusterRow({
  cluster,
  open,
  onToggle,
}: {
  cluster: AdminCluster;
  open: boolean;
  onToggle: () => void;
}) {
  const patch = usePatchCluster();
  const members = useAdminClusterMembers(open ? cluster.id : null);
  const [title, setTitle] = useState(cluster.title);
  const [summary, setSummary] = useState(cluster.summary);
  const [proposed, setProposed] = useState(cluster.proposed_change ?? "");

  useEffect(() => {
    setTitle(cluster.title);
    setSummary(cluster.summary);
    setProposed(cluster.proposed_change ?? "");
  }, [cluster.id, cluster.title, cluster.summary, cluster.proposed_change]);

  const apply = (body: PatchClusterIn) =>
    patch.mutate({ id: cluster.id, body });

  return (
    <li className="rounded-2xl border border-slate-800 bg-slate-950/40">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start justify-between gap-3 p-4 text-left"
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span
            className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${STATUS_TONE[cluster.status]}`}
          >
            {cluster.status.replace("_", " ")}
          </span>
          <span className="truncate text-sm text-slate-100">{cluster.title}</span>
          <span className="shrink-0 text-[11px] text-slate-500">
            · {cluster.member_count} item{cluster.member_count === 1 ? "" : "s"}
          </span>
        </div>
        <span className="shrink-0 text-[11px] text-slate-500">
          run {fmt(cluster.llm_run_at)}
        </span>
      </button>

      {open && (
        <div className="space-y-3 border-t border-slate-800 p-4 text-xs">
          <div>
            <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-slate-500">
              Title
            </label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() => title !== cluster.title && apply({ title })}
              className="w-full rounded-lg border border-slate-800 bg-slate-950 px-2 py-1.5 text-xs text-slate-100"
            />
          </div>
          <div>
            <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-slate-500">
              Summary
            </label>
            <textarea
              value={summary}
              rows={3}
              onChange={(e) => setSummary(e.target.value)}
              onBlur={() => summary !== cluster.summary && apply({ summary })}
              className="w-full resize-y rounded-lg border border-slate-800 bg-slate-950 px-2 py-1.5 text-xs text-slate-100"
            />
          </div>
          <div>
            <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-slate-500">
              Proposed change
            </label>
            <textarea
              value={proposed}
              rows={3}
              onChange={(e) => setProposed(e.target.value)}
              onBlur={() =>
                proposed !== (cluster.proposed_change ?? "") &&
                apply({ proposed_change: proposed })
              }
              className="w-full resize-y rounded-lg border border-slate-800 bg-slate-950 px-2 py-1.5 text-xs text-slate-100"
            />
          </div>
          <div>
            <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-slate-500">
              Status
            </label>
            <select
              value={cluster.status}
              onChange={(e) =>
                apply({ status: e.target.value as AdminCluster["status"] })
              }
              className="w-full rounded-lg border border-slate-800 bg-slate-950 px-2 py-1.5 text-xs text-slate-200"
            >
              {CLUSTER_STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s.replace("_", " ")}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-slate-500">
              Members
            </div>
            {members.isLoading ? (
              <div className="text-slate-500">Loading…</div>
            ) : (members.data ?? []).length === 0 ? (
              <div className="text-slate-500">No members.</div>
            ) : (
              <ul className="space-y-1 text-slate-300">
                {members.data!.map((m) => (
                  <li key={m.id} className="truncate">
                    <span className="font-mono text-[10px] text-slate-500">
                      #{m.id}
                    </span>{" "}
                    <span className="capitalize text-slate-500">{m.kind}</span>{" "}
                    {m.title}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {patch.isError && (
            <div className="text-rose-300">
              Couldn&apos;t save: {patch.error.message}
            </div>
          )}
        </div>
      )}
    </li>
  );
}
